import './ScalingAnalysisTable.scss';
import { ReactElement, useMemo } from 'react';
import { FiDownload } from 'react-icons/fi';
import { formatMoney, formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { Button } from '../../../Components/Button/Button';
import { Appearance, Design, Size } from '../../../Components/designConstants';
import { BPMTable, SuperTopItemType, TypedHeader } from '../../../Components/StickyTable';
import { downloadJSONToCSV } from '../../../utils/download-utils';
import { formatSpend } from '../forecasting-constants';
import { marginalPerDollar } from './scaling-math';
import { ScalingCurveParams, ScalingCurvePoint } from './scalingData';

// Table row augmented with the marginal ROAS (curve slope) at that spend level — null for volume
// outcomes and wherever the slope isn't defined.
type ScalingTableRow = ScalingCurvePoint & { marginalRoas: number | null };

interface ScalingAnalysisTableProps {
  // Precomputed model curve from the scaling pipeline (one row per spend increment).
  curve: ScalingCurvePoint[];
  // The fit's a/b, for exact marginal ROAS (absent on older runs).
  params?: ScalingCurveParams;
  efficiencyMetric: string; // 'CPA' | 'ROAS' — labels the efficiency column.
  channel: string;
  platform: string;
  tactic: string;
  kpi: string;
}

const ROAS = 'ROAS';

// Volume fits are counts; revenue fits are dollars → show a $.
const fittedFormatter = (isRevenue: boolean) => (value: number) =>
  isRevenue ? formatMoney(value) : formatNumber(value, 0);

// CPA = spend per unit (spend / fit); ROAS = return per spend $ (fit / spend).
const efficiencyValue = (row: ScalingCurvePoint, metric: string): number | null => {
  if (metric === ROAS) return row.spend > 0 ? row.fit / row.spend : null;
  return row.fit > 0 ? row.spend / row.fit : null;
};

const formatEfficiency = (value: number | null, metric: string): string => {
  if (value === null) return '--';
  return metric === ROAS ? `${value.toFixed(2)}x` : formatMoney(value);
};

// Marginal ROAS = the fitted revenue curve's slope at that spend (Δrevenue/Δspend) — the next
// dollar's return. Sits alongside the average ROAS column so scaling decisions read off the margin.
const formatMarginal = (value: number | null): string =>
  value === null ? '--' : `${value.toFixed(2)}x`;

export const ScalingAnalysisTable = ({
  curve,
  params,
  efficiencyMetric,
  channel,
  platform,
  tactic,
  kpi
}: ScalingAnalysisTableProps): ReactElement => {
  const isRevenue = efficiencyMetric === ROAS;

  // Curve rows augmented with marginal ROAS (revenue only). Shared by the grid and CSV export.
  const tableRows: ScalingTableRow[] = useMemo(
    () =>
      curve.map((row, i) => ({
        ...row,
        marginalRoas: isRevenue ? marginalPerDollar(curve, i, params) : null
      })),
    [curve, params, isRevenue]
  );

  const [headers, superHeaders] = useMemo(() => {
    const base = { flex: true };
    const formatFit = fittedFormatter(isRevenue);

    const tableHeaders: TypedHeader<ScalingTableRow>[] = [
      { ...base, name: 'spend', label: 'Spend', renderer: row => formatSpend(row.spend) },
      { ...base, name: 'fit', label: 'Predicted Values', renderer: row => formatFit(row.fit) },
      { ...base, name: 'lwrConf', label: 'Lower Limit', renderer: row => formatFit(row.lwrConf) },
      { ...base, name: 'uprConf', label: 'Upper Limit', renderer: row => formatFit(row.uprConf) },
      {
        ...base,
        name: 'efficiency',
        label: efficiencyMetric,
        renderer: row => formatEfficiency(efficiencyValue(row, efficiencyMetric), efficiencyMetric)
      },
      // Marginal ROAS only applies to a revenue curve (a volume curve has no 1x margin read).
      ...(isRevenue
        ? [
            {
              ...base,
              name: 'marginalRoas',
              label: 'Marginal ROAS',
              renderer: (row: ScalingTableRow) => formatMarginal(row.marginalRoas)
            }
          ]
        : []),
      { ...base, name: 'extrapolated', label: 'Data Source', renderer: row => row.extrapolated }
    ];

    // BPMTable's default superHeader renderer renders `data` directly, so it must be a string.
    // Spans cover: [Spend, Predicted Values], [Lower, Upper] confidence, then the efficiency
    // group — [efficiency, (Marginal ROAS,) Data Source], which is 3 wide for revenue, 2 for volume.
    const tableSuperHeaders: SuperTopItemType<string>[] = [
      { span: 2, data: '' },
      { span: 2, data: 'Confidence Interval' },
      { span: isRevenue ? 3 : 2, data: '' }
    ];

    return [tableHeaders, tableSuperHeaders];
  }, [efficiencyMetric, isRevenue]);

  // Download the rows as shown (raw numbers, not formatted).
  const exportRows = useMemo(
    () =>
      tableRows.map(row => {
        const efficiency = efficiencyValue(row, efficiencyMetric);
        return {
          Spend: row.spend,
          'Predicted Values': row.fit,
          'Confidence Lower': row.lwrConf,
          'Confidence Upper': row.uprConf,
          [efficiencyMetric]: efficiency === null ? '' : Math.round(efficiency * 100) / 100,
          ...(isRevenue
            ? {
                'Marginal ROAS':
                  row.marginalRoas === null ? '' : Math.round(row.marginalRoas * 100) / 100
              }
            : {}),
          'Data Source': row.extrapolated
        };
      }),
    [tableRows, efficiencyMetric, isRevenue]
  );

  const fileBase = `scaling_${channel}_${platform}_${tactic}_${kpi}`.replace(/[^a-z0-9_]+/gi, '_');

  return (
    <div className='scaling-analysis-table'>
      <div className='scaling-analysis-table-header'>
        <span className='scaling-analysis-table-title'>
          {`Predicted ${kpi} ${isRevenue ? 'Revenue' : 'Volume'} by Spend Level`}
        </span>
        <div className='scaling-analysis-table-controls'>
          <Button
            appearance={Appearance.EMPTY}
            design={Design.NEUTRAL}
            size={Size.SM}
            leadingElement={<FiDownload />}
            onClick={() => downloadJSONToCSV(exportRows, fileBase)}
          >
            CSV
          </Button>
        </div>
      </div>
      <div className='scaling-analysis-table-grid'>
        <BPMTable
          data={tableRows}
          headers={headers}
          superHeaders={superHeaders}
          filterBar={false}
        />
      </div>
    </div>
  );
};
